import _ from 'lodash';

import {
  Experiment,
  RegionalExperimentConfigValues,
  Variable,
} from '../domain';
import { getAllDomains } from '@/common/domains';
import { REQUESTED, VariableType } from '@/constants';
import { FACETS } from '@/constants/facets';
import Service from '@/lib/App';
import {
  Choice,
  Constraint,
  DomainMetadata,
  VariableInfo,
} from '@/lib/PlannerDTOs';

export * from './analysis';

export const EXPERIMENTS_DEFAULT_PAGE_SIZE = 20;

export const ExperimentFormMode = {
  CREATE: 'create',
  EDIT: 'edit',
  CLONE: 'clone',
  VIEW: 'view',
} as const;

export type ExperimentFormModeType =
  (typeof ExperimentFormMode)[keyof typeof ExperimentFormMode];

export const TreatmentDefaultColors = [
  '#E8E8E8',
  '#A29BFE',
  '#4FC3F7',
  '#F3B1CD',
  '#58B19F',
  '#656F94',
  '#EFC6FC',
  '#F0D5BA',
  '#A6868F',
  '#0D47A1',
  '#BAD5F0',
  '#EDAB9B',
  '#4834D4',
  '#82589F',
  '#0288D1',
];

export const TreatmentIds = [
  'Control', // 'Control' is the default treatment id, and it is not allowed to be changed.
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
];

export const BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS = 3;

export const canEditExperiment = (experiment: Experiment) => {
  return experiment.state === REQUESTED;
};

export const overlapByDefault = (experiment: Experiment) => {
  const isDomainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
  const isDomainOverlapSupported = Service.cache.isDomainOverlapSupported(
    experiment?.domain,
  );
  const isTimeSliceStrategy = [
    ExperimentAssignmentStrategy.TIME_SLICE,
    ExperimentAssignmentStrategy.TIME_SLICE_V2,
    ExperimentAssignmentStrategy.TIME_SLICE_GROUPED,
    ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
    ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
    ExperimentAssignmentStrategy.EXPLICIT_RANGE,
  ].includes(experiment?.variables?.[0]?.strategy);

  return (
    (isDomainOverlapSupported || isDomainlessFeatureFlag) &&
    !isTimeSliceStrategy
  );
};

export const ExperimentAssignmentStrategy = {
  UNIFORM_CHOICE: 'uniformChoice',
  WEIGHTED_CHOICE: 'weightedChoice',
  EXPLICIT_GROUP: 'explicitGroup',
  CUSTOM_TIME_SLICE: 'customTimeSlice',
  TIME_SLICE_SHUFFLE: 'timeSliceShuffle',
  // legacy strategies
  GEO_TIME_SLICE_SHUFFLE: 'geoTimeSliceShuffle',
  TIME_SLICE: 'timeSlice',
  TIME_SLICE_V2: 'timeSliceV2',
  TIME_SLICE_GROUPED: 'timeSliceGrouped',
  EXPLICIT_RANGE: 'explicitRange',
};

export const isPercentageSplitStrategy = (strategy: string) => {
  return [
    ExperimentAssignmentStrategy.UNIFORM_CHOICE,
    ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
  ].includes(strategy);
};

export const isTimeSliceStrategy = (strategy: string) => {
  return [
    ExperimentAssignmentStrategy.TIME_SLICE,
    ExperimentAssignmentStrategy.TIME_SLICE_V2,
    ExperimentAssignmentStrategy.TIME_SLICE_GROUPED,
    ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
    ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
    ExperimentAssignmentStrategy.EXPLICIT_RANGE,
  ].includes(strategy);
};

export const isLegacyTimeSliceStrategy = (strategy: string) => {
  return [
    ExperimentAssignmentStrategy.TIME_SLICE,
    ExperimentAssignmentStrategy.TIME_SLICE_V2,
    ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    ExperimentAssignmentStrategy.TIME_SLICE_GROUPED,
  ].includes(strategy);
};

export const isTimeSliceShuffleStrategy = (strategy: string) => {
  return [
    ExperimentAssignmentStrategy.TIME_SLICE,
    ExperimentAssignmentStrategy.TIME_SLICE_V2,
    ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
  ].includes(strategy);
};

export const isBalancedShuffleTimeSliceStrategy = (strategy: string) => {
  return strategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE;
};

export const ExperimentAction = {
  CREATE: 'create',
  EDIT: 'edit',
  OVERVIEW: 'overview',
  OVERLAP: 'overlap',
  APPROVAL: 'approval',
  CLONE: 'clone',
  AXA_SCORECARDS: 'axa-scorecards',
};

export const isViewMode = (action: string) => {
  return [
    ExperimentAction.OVERVIEW,
    ExperimentAction.APPROVAL,
    ExperimentAction.OVERLAP,
    ExperimentAction.AXA_SCORECARDS,
  ].includes(action);
};

export const isCreateMode = (action: string) => {
  return [ExperimentAction.CREATE, ExperimentAction.CLONE].includes(action);
};

export const isEditMode = (action: string) => {
  return [ExperimentAction.EDIT].includes(action);
};

export const isOverviewMode = (action: string) => {
  return [ExperimentAction.OVERVIEW].includes(action);
};

export const RestrictedBy = {
  COUNTRY: 'ctry',
  REGION: 'reg',
  MULTI: 'multi',
  CITY: 'city',
};

export const ExperimentState = {
  REQUESTED: 'REQUESTED',
  SCHEDULED: 'SCHEDULED',
  STARTED: 'STARTED',
  PAUSED: 'PAUSED',
  COMPLETED: 'COMPLETED',
  EARLY_COMPLETE: 'EARLY_COMPLETE',
  ABORTED: 'ABORTED',
  CANCELLED: 'CANCELLED',
  UNKNOWN: 'UNKNOWN',
};

export const PopulationTargetType = {
  CITY: 'city',
  COUNTRY: 'ctry',
  REGIONAL: 'reg',
};

export const PopulationTargetTypes = [
  PopulationTargetType.CITY,
  PopulationTargetType.COUNTRY,
  PopulationTargetType.REGIONAL,
] as const;

export const PopulationTargetName = {
  [PopulationTargetType.CITY]: 'City',
  [PopulationTargetType.COUNTRY]: 'Country',
  [PopulationTargetType.REGIONAL]: 'Regional',
};

export const getGeography = (currentDomainName) => {
  if (!currentDomainName) return null;
  const allDomains = getAllDomains();
  const domain = allDomains.find((d) => d.name === currentDomainName);
  return domain?.restrictBy ?? null;
};

export const getSupportedPopulationTargets = (
  currentDomainName: string,
): string[] => {
  const geography = getGeography(currentDomainName);

  if (
    geography &&
    PopulationTargetTypes.includes(
      geography as (typeof PopulationTargetTypes)[number],
    )
  ) {
    return [geography];
  }

  return [...PopulationTargetTypes];
};

export const getDefaultPopulationTarget = (
  currentDomainName: string,
): string => {
  return getSupportedPopulationTargets(currentDomainName)[0];
};

export const isRestrictByConstraint = (constraint: Constraint) => {
  return (
    constraint.target === PopulationTargetType.REGIONAL ||
    constraint.target === PopulationTargetType.COUNTRY ||
    constraint.target === PopulationTargetType.CITY
  );
};

export const isExperimentTimeConstraint = (constraint: Constraint) => {
  return constraint.target === FACETS.Timestamp;
};

export const getCountryConstraint = (experiment: Experiment): Constraint => {
  const constraints = experiment?.constraints;

  if (constraints?.length === 0) return null;

  return constraints?.find((c) => c.target === PopulationTargetType.COUNTRY);
};

export const getRestrictByConstraint = (experiment: Experiment): Constraint => {
  const constraints = experiment?.constraints;

  if (constraints?.length === 0) return null;

  const regionalConstraint = constraints?.find(
    (c) => c.target === PopulationTargetType.REGIONAL,
  );

  if (regionalConstraint) return regionalConstraint;

  const countryConstraint = constraints?.find(
    (c) => c.target === PopulationTargetType.COUNTRY,
  );
  if (countryConstraint) return countryConstraint;

  const cityConstraint = constraints?.find(
    (c) => c.target === PopulationTargetType.CITY,
  );
  if (cityConstraint) return cityConstraint;

  return null;
};

export const getExperimentStartTimeConstraint = (
  experiment: Experiment,
): Constraint => {
  const constraints = experiment?.constraints;

  if (!constraints?.length) return null;

  return constraints?.find(
    (c) => c.target === FACETS.Timestamp && c.op === '>',
  );
};

export const getExperimentEndTimeConstraint = (
  experiment: Experiment,
): Constraint => {
  const constraints = experiment?.constraints;

  if (!constraints?.length) return null;

  return constraints?.find(
    (c) => c.target === FACETS.Timestamp && c.op === '<',
  );
};
export const getExperimentStartAndEndTime = (
  experiment: Experiment,
): [number, number] => {
  const constraints = experiment?.constraints;

  if (!constraints?.length) return [null, null];

  let start: number | null = null;
  let end: number | null = null;
  const startTimeConstraint = getExperimentStartTimeConstraint(experiment);
  const endTimeConstraint = getExperimentEndTimeConstraint(experiment);

  if (startTimeConstraint) start = Number(startTimeConstraint.value);
  if (endTimeConstraint) end = Number(endTimeConstraint.value);

  return [start, end];
};

export const PopulationTargetMode = {
  STATIC: 'static',
  DYNAMIC: 'dynamic',
};

export const getTreatmentName = (choice: Choice, idx: number) => {
  if (choice?.name) return choice.name;

  return TreatmentIds[idx] === 'Control'
    ? 'Control'
    : `Treatment ${TreatmentIds[idx]}`;
};

export const RegionalCountries = [
  'KH',
  'ID',
  'MY',
  'MM',
  'PH',
  'SG',
  'TH',
  'VN',
];

export const getVariables = (
  configurations: RegionalExperimentConfigValues[],
): Variable[] => {
  return configurations?.[0]?.variables || [];
};

export const getAssignmentStrategy = (
  configurations: RegionalExperimentConfigValues[],
): string => {
  const variables = getVariables(configurations);
  return variables?.[0]?.strategy || '';
};

export const getDomain = (domainName: string): DomainMetadata => {
  return getAllDomains().find((domain) => domain.name === domainName);
};

export function getAssignByFacetFromDomain(domainName: string) {
  const assignByFacet = getDomain(domainName)?.targetBy;

  return Service.cache
    ?.getAllFacets()
    .find((facet) => facet.name === assignByFacet);
}

export function emptyValuesVariables(
  configurations: RegionalExperimentConfigValues[],
  variables: VariableInfo[],
): string[] {
  const emptyVariableNames = [];
  const stringVariableTypes = [
    VariableType.STRING,
    VariableType.JSON,
    VariableType.MAP,
  ];
  const numberVariableTypes = [
    VariableType.FLOAT64,
    VariableType.INT64,
    VariableType.BOOLEAN,
  ];

  configurations?.forEach((c: RegionalExperimentConfigValues) => {
    c?.variables?.forEach((cv) => {
      const variable = variables.find((v) => v.name === cv.name);

      cv?.choices?.forEach((cc) => {
        if (
          numberVariableTypes.includes(variable?.type as VariableType) &&
          cc.value == null
        ) {
          emptyVariableNames.push(variable.name);
        } else if (
          stringVariableTypes.includes(variable?.type as VariableType) &&
          !cc.string
        )
          emptyVariableNames.push(variable.name);
      });
    });
  });

  return _.uniq(emptyVariableNames);
}

export const CustomTimeSliceStrategy = {
  BALANCED_SHUFFLE: 'BalancedShuffle',
};

export const isCompatibleExperiment = (experimentPayload: Experiment) => {
  if (!experimentPayload) {
    return false;
  }

  if (!experimentPayload.variables?.length) {
    return false;
  }

  if (experimentPayload.variables.length === 1) {
    return true;
  }

  // different variables have different number of treatments
  if (experimentPayload.variables?.length > 1) {
    const firstVariableTreatmentsCount =
      experimentPayload.variables?.[0]?.choices?.length;
    const allVariablesHaveSameTreatmentAmount =
      experimentPayload.variables?.every(
        (variable) => variable.choices?.length === firstVariableTreatmentsCount,
      );
    if (!allVariablesHaveSameTreatmentAmount) {
      return false;
    }
  }
  // different variables have different strategy
  if (experimentPayload.variables?.length > 1) {
    const firstVariableStrategy = experimentPayload.variables?.[0]?.strategy;
    const allVariablesHaveSameStrategy = experimentPayload.variables?.every(
      (variable) => variable.strategy === firstVariableStrategy,
    );
    if (!allVariablesHaveSameStrategy) {
      return false;
    }
  }
  // different variables have different facets
  if (experimentPayload.variables?.length > 1) {
    const firstVariableFacet = experimentPayload.variables?.[0]?.facets;
    const allVariablesHaveSameFacet = experimentPayload.variables?.every(
      (variable) =>
        variable.facets?.sort().join(',') ===
        firstVariableFacet?.sort().join(','),
    );
    if (!allVariablesHaveSameFacet) {
      return false;
    }
  }

  return true;
};
